<script setup>
import { ref, onMounted, computed } from 'vue';
import { useStore } from 'vuex';
import { useMapGetter } from 'dashboard/composables/store';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import SlaComponent from '../components/SlaComponent.vue';
import MultiSelectFilter from '../components/MultiSelectFilter.vue';
import SelectedFiltersDisplay from '../components/SelectedFiltersDisplay.vue';
import WootDateRangePicker from 'dashboard/components/ui/DateRangePicker.vue';

const { t } = useI18n();

const store = useStore();
const router = useRouter();

const loading = ref(false);
const slaData = useMapGetter('tickets/getSlaMonitoringData');

// Filter state
const filters = ref({
  team_id: [],
  status: [],
  created_at_from: null,
  created_at_to: null,
});

const dateRange = ref([]);

// Filter data
const teams = computed(() => store.getters['teams/getMyTeams'] || []);
const statuses = computed(() => store.getters['tickets/getStatuses'] || []);

const filterData = computed(() => ({
  team_id: teams.value || [],
  status: statuses.value || [],
}));

// Computed properties
const summary = computed(() => {
  const appliedSlas = slaData.value.applied_slas || [];
  const tickets = slaData.value.tickets || [];

  const totalAppliedSlas = appliedSlas.length;
  const activeSlas = appliedSlas.filter(
    sla => sla.sla_status === 'active'
  ).length;
  const hitSlas = appliedSlas.filter(sla => sla.sla_status === 'hit').length;
  const missedSlas = appliedSlas.filter(
    sla => sla.sla_status === 'missed'
  ).length;

  const totalResponses = tickets.reduce(
    (sum, ticket) => sum + (ticket.agent_response_count || 0),
    0
  );
  const ticketsWithResponses = tickets.filter(
    ticket => (ticket.agent_response_count || 0) > 0
  ).length;
  const averageResponses =
    totalAppliedSlas > 0 ? (totalResponses / totalAppliedSlas).toFixed(2) : 0;

  return {
    totalAppliedSlas,
    activeSlas,
    hitSlas,
    missedSlas,
    totalResponses,
    ticketsWithResponses,
    averageResponses,
  };
});

const recentEvents = computed(() => {
  return (slaData.value.sla_events || [])
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
    .slice(0, 10);
});

const problematicTickets = computed(() => {
  return (slaData.value.tickets || [])
    .filter(ticket =>
      ['missed', 'active_with_misses'].includes(ticket.sla_status)
    )
    .map(ticket => ({
      ...ticket,
      sla_policy_name: ticket.sla_policy?.name || 'N/A',
      first_response_at: ticket.first_agent_response_at,
    }));
});

// Methods
const buildFilterParams = () => {
  const params = {};

  if (filters.value.team_id && filters.value.team_id.length > 0) {
    params.team_id = filters.value.team_id;
  }

  if (filters.value.status && filters.value.status.length > 0) {
    params.status = filters.value.status;
  }

  if (filters.value.created_at_from) {
    params.created_at_from = filters.value.created_at_from;
  }

  if (filters.value.created_at_to) {
    params.created_at_to = filters.value.created_at_to;
  }

  return params;
};

const fetchSlaData = async () => {
  try {
    loading.value = true;
    const filterParams = buildFilterParams();
    await store.dispatch('tickets/getSlaMonitoringData', filterParams);
  } catch (error) {
  } finally {
    loading.value = false;
  }
};

const refreshData = () => {
  fetchSlaData();
};

// Helper methods

const getSlaStatusText = status => {
  const statusMap = {
    active: 'Ativo',
    hit: 'Cumprido',
    missed: 'Perdido',
    active_with_misses: 'Ativo com Perdas',
  };
  return statusMap[status] || status;
};

const getSlaStatusClass = status => {
  const classMap = {
    active: 'bg-blue-100 text-blue-800',
    hit: 'bg-green-100 text-green-800',
    missed: 'bg-red-100 text-red-800',
    active_with_misses: 'bg-yellow-100 text-yellow-800',
  };
  return classMap[status] || 'bg-gray-100 text-gray-800';
};

const formatDate = dateString => {
  if (!dateString) return t('TICKETS.SLA.DATE_NOT_AVAILABLE');
  const date = new Date(dateString);
  return date.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

const navigateToTicket = ticketId => {
  router.push({ name: 'helpdesk_ticket_view', params: { id: ticketId } });
};

const getSlaPerformance = () => {
  if (summary.value.totalAppliedSlas === 0) return 0;
  return (
    (summary.value.hitSlas / summary.value.totalAppliedSlas) *
    100
  ).toFixed(2);
};
// Filter handlers
const handleTeamSelect = item => {
  const currentValues = filters.value.team_id || [];
  const index = currentValues.indexOf(item.id);

  if (index > -1) {
    currentValues.splice(index, 1);
  } else {
    currentValues.push(item.id);
  }

  filters.value.team_id = currentValues;
  fetchSlaData();
};

const handleStatusSelect = item => {
  const currentValues = filters.value.status || [];
  const index = currentValues.indexOf(item.name);

  if (index > -1) {
    currentValues.splice(index, 1);
  } else {
    currentValues.push(item.name);
  }

  filters.value.status = currentValues;
  fetchSlaData();
};

const formatDateForApi = date => {
  if (!date) return null;
  const dateObj = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(dateObj.getTime())) return null;
  return dateObj.toISOString().split('T')[0];
};

const handleDateRangeChange = dates => {
  dateRange.value = dates;
  if (dates && dates.length === 2) {
    filters.value.created_at_from = formatDateForApi(dates[0]);
    filters.value.created_at_to = formatDateForApi(dates[1]);
  } else {
    filters.value.created_at_from = null;
    filters.value.created_at_to = null;
  }
  fetchSlaData();
};

const getSelectedItems = (filterKey, options) => {
  const selectedValues = filters.value[filterKey] || [];
  if (filterKey === 'team_id') {
    return options.filter(option => selectedValues.includes(option.id));
  }
  if (filterKey === 'status') {
    return options.filter(option => selectedValues.includes(option.name));
  }
  return [];
};

const selectedFilters = computed(() => {
  const tags = [];

  // Team filters
  if (filters.value.team_id && filters.value.team_id.length > 0) {
    filters.value.team_id.forEach(teamId => {
      const team = teams.value.find(_team => _team.id === teamId);
      if (team) {
        tags.push({
          type: 'team_id',
          key: 'team_id',
          value: teamId,
          label: team.name,
          color: 'purple',
        });
      }
    });
  }

  // Status filters
  if (filters.value.status && filters.value.status.length > 0) {
    filters.value.status.forEach(statusName => {
      const status = statuses.value.find(s => s.name === statusName);
      if (status) {
        tags.push({
          type: 'status',
          key: 'status',
          value: statusName,
          label: status.name,
          color: 'blue',
        });
      }
    });
  }

  // Date range filter
  if (filters.value.created_at_from || filters.value.created_at_to) {
    const formatDateLabel = dateString => {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
    };

    const fromLabel = filters.value.created_at_from
      ? formatDateLabel(filters.value.created_at_from)
      : 'Início';
    const toLabel = filters.value.created_at_to
      ? formatDateLabel(filters.value.created_at_to)
      : 'Fim';
    tags.push({
      type: 'date_range',
      key: 'date_range',
      value: {
        from: filters.value.created_at_from,
        to: filters.value.created_at_to,
      },
      label: `${fromLabel} - ${toLabel}`,
      color: 'green',
    });
  }

  return tags;
});

const removeFilter = filter => {
  if (filter.key === 'team_id') {
    filters.value.team_id = filters.value.team_id.filter(
      id => id !== filter.value
    );
  } else if (filter.key === 'status') {
    filters.value.status = filters.value.status.filter(
      name => name !== filter.value
    );
  } else if (filter.key === 'date_range') {
    filters.value.created_at_from = null;
    filters.value.created_at_to = null;
    dateRange.value = [];
  }
  fetchSlaData();
};

const handleClearFilters = () => {
  filters.value = {
    team_id: [],
    status: [],
    created_at_from: null,
    created_at_to: null,
  };
  dateRange.value = [];
  fetchSlaData();
};

const getTotalSlasPerSlaPolicyTooltip = () => {
  // Lets show the total of slas for each sla policy
  const slaPolicies = slaData.value.applied_slas.map(
    sla => sla.sla_policy?.name
  );
  const uniqueSlaPolicies = [...new Set(slaPolicies)];
  const totalSlasPerSlaPolicy = uniqueSlaPolicies.map(slaPolicy => {
    return {
      name: slaPolicy,
      total: slaData.value.applied_slas.filter(
        sla => sla.sla_policy?.name === slaPolicy
      ).length,
    };
  });

  return totalSlasPerSlaPolicy
    .map(slaPolicy => `${slaPolicy.name}: ${slaPolicy.total}`)
    .join(`<br />`);
};
// lets unify this code with the other tooltips
const getSlaPolicyStatusTotalTooltip = slaStatus => {
  const slaPolicies = slaData.value.applied_slas.map(
    sla => sla.sla_policy?.name
  );
  const uniqueSlaPolicies = [...new Set(slaPolicies)];
  const slaPolicyList = uniqueSlaPolicies.map(policyName => {
    return {
      name: policyName,
      total: slaData.value.applied_slas.filter(
        sla =>
          sla.sla_policy?.name === policyName && sla.sla_status === slaStatus
      ).length,
    };
  });
  return slaPolicyList
    .filter(policy => policy.total > 0)
    .map(policy => `${policy.name}: ${policy.total}`)
    .join(`<br />`);
};

// Lifecycle
onMounted(async () => {
  // Fetch filter options
  try {
    await store.dispatch('teams/get');
  } catch (err) {
    // Error is handled by the store action
  }

  try {
    await store.dispatch('tickets/getStatuses');
  } catch (err) {
    // Error is handled by the store action
  }

  fetchSlaData();
});
</script>

<template>
  <div class="flex flex-col w-full h-full bg-n-background">
    <!-- Header -->
    <div class="px-8 py-4 border-b border-n-weak">
      <h2 class="text-2xl font-bold text-n-slate-12">
        {{ t('TICKETS.SLA.MONITORING_DASHBOARD') }}
      </h2>
      <p class="text-n-slate-11">
        {{ t('TICKETS.SLA.MONITORING_DESCRIPTION') }}
      </p>
    </div>
    <!-- Filters Section -->
    <div class="px-8 pt-2 border-b border-n-weak">
      <div class="flex flex-col lg:flex-row gap-4">
        <div class="flex-1 min-w-0 overflow-x-auto">
          <div class="flex gap-2 p-2">
            <MultiSelectFilter
              :options="filterData.team_id"
              :selected-items="getSelectedItems('team_id', filterData.team_id)"
              multiselector-title="Filtrar"
              multiselector-placeholder="Time"
              input-placeholder="Buscar time..."
              icon=""
              @select="handleTeamSelect"
            />

            <MultiSelectFilter
              :options="filterData.status"
              :selected-items="getSelectedItems('status', filterData.status)"
              multiselector-title="Filtrar"
              multiselector-placeholder="Status"
              input-placeholder="Buscar status..."
              icon=""
              @select="handleStatusSelect"
            />

            <div class="relative">
              <WootDateRangePicker
                :value="dateRange"
                confirm-text="Confirmar"
                placeholder="Data de Criação"
                @change="handleDateRangeChange"
              />
            </div>
          </div>
        </div>
      </div>
      <!-- Selected Filters Display -->
      <div class="border-1 border-n-weak rounded-lg p-1 mb-1">
        <SelectedFiltersDisplay
          v-if="selectedFilters.length > 0"
          :selected-filters="selectedFilters"
          show-clear-all
          @remove-filter="removeFilter"
          @clear-all="handleClearFilters"
        />
      </div>
    </div>

    <!-- Scrollable Content -->
    <div class="flex-1 overflow-auto">
      <div class="px-8 py-6 max-w-6xl justify-self-center">
        <!-- Summary Cards -->
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-4">
          <div
            v-tooltip="{
              content: getTotalSlasPerSlaPolicyTooltip(),
              placement: 'top',
              html: true,
            }"
            class="bg-n-solid-2 rounded-lg p-4"
          >
            <div class="flex items-center justify-between">
              <div>
                <p class="text-sm font-medium text-n-slate-11">
                  {{ t('TICKETS.SLA.TOTAL_SLAS') }}
                </p>
                <p class="text-2xl font-bold text-n-slate-12">
                  {{ summary.totalAppliedSlas }}
                </p>
              </div>
              <div class="p-3 bg-blue-100 rounded-full">
                <svg
                  class="w-6 h-6 text-blue-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
            </div>
          </div>

          <div
            v-tooltip="{
              content: getSlaPolicyStatusTotalTooltip('active'),
              placement: 'top',
              html: true,
            }"
            class="bg-n-solid-2 rounded-lg p-4"
          >
            <div class="flex items-center justify-between">
              <div>
                <p class="text-sm font-medium text-n-slate-11">
                  {{ t('TICKETS.SLA.ACTIVE_SLAS') }}
                </p>
                <p class="text-2xl font-bold text-green-600">
                  {{ summary.activeSlas }}
                </p>
              </div>
              <div class="p-3 bg-green-100 rounded-full">
                <svg
                  class="w-6 h-6 text-green-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M13 10V3L4 14h7v7l9-11h-7z"
                  />
                </svg>
              </div>
            </div>
          </div>

          <div
            v-tooltip="{
              content: getSlaPolicyStatusTotalTooltip('hit'),
              placement: 'top',
              html: true,
            }"
            class="bg-n-solid-2 rounded-lg p-4"
          >
            <div class="flex items-center justify-between">
              <div>
                <p class="text-sm font-medium text-n-slate-11">
                  {{ t('TICKETS.SLA.HIT_SLAS') }}
                </p>
                <p class="text-2xl font-bold text-green-600">
                  {{ summary.hitSlas }}
                </p>
              </div>
              <div class="p-3 bg-green-100 rounded-full">
                <svg
                  class="w-6 h-6 text-green-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
            </div>
          </div>

          <div
            v-tooltip="{
              content: getSlaPolicyStatusTotalTooltip('missed'),
              placement: 'top',
              html: true,
            }"
            class="bg-n-solid-2 rounded-lg p-4"
          >
            <div class="flex items-center justify-between">
              <div>
                <p class="text-sm font-medium text-n-slate-11">
                  {{ t('TICKETS.SLA.MISSED_SLAS') }}
                </p>
                <p class="text-2xl font-bold text-red-600">
                  {{ summary.missedSlas }}
                </p>
              </div>
              <div class="p-3 bg-red-100 rounded-full">
                <svg
                  class="w-6 h-6 text-red-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
            </div>
          </div>

          <div class="bg-n-solid-2 rounded-lg p-4">
            <div class="flex items-center justify-between">
              <div>
                <p class="text-sm font-medium text-n-slate-11">
                  {{ t('TICKETS.SLA.PERFORMANCE') }}
                </p>
                <p
                  v-if="getSlaPerformance() >= 80"
                  class="text-2xl font-bold text-green-600"
                >
                  {{ getSlaPerformance() }}{{ t('TICKETS.SLA.PERCENT') }}
                </p>
                <p v-else class="text-2xl font-bold text-red-600">
                  {{ getSlaPerformance() }}{{ t('TICKETS.SLA.PERCENT') }}
                </p>
              </div>
              <div
                v-if="getSlaPerformance() >= 80"
                class="p-3 bg-green-100 rounded-full"
              >
                <svg
                  class="w-6 h-6 text-green-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M2.25 18 9 11.25l4.306 4.306a11.95 11.95 0 0 1 5.814-5.518l2.74-1.22m0 0-5.94-2.281m5.94 2.28-2.28 5.941"
                  />
                </svg>
              </div>
              <div v-else class="p-3 bg-red-100 rounded-full">
                <svg
                  class="w-6 h-6 text-red-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
            </div>
          </div>
        </div>

        <!-- Agent Response Metrics -->
        <div class="bg-n-solid-2 rounded-lg p-6 mb-4 border-b border-n-weak">
          <h3 class="text-lg font-semibold text-n-slate-12 mb-4">
            {{ t('TICKETS.SLA.AGENT_RESPONSE_METRICS') }}
          </h3>
          <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div class="text-center">
              <p class="text-3xl font-bold text-n-slate-12">
                {{ summary.totalResponses }}
              </p>
              <p class="text-sm text-n-slate-11">
                {{ t('TICKETS.SLA.TOTAL_RESPONSES') }}
              </p>
            </div>
            <div class="text-center">
              <p class="text-3xl font-bold text-n-slate-12">
                {{ summary.ticketsWithResponses }}
              </p>
              <p class="text-sm text-n-slate-11">
                {{ t('TICKETS.SLA.TICKETS_WITH_RESPONSES') }}
              </p>
            </div>
            <div class="text-center">
              <p class="text-3xl font-bold text-n-slate-12">
                {{ summary.averageResponses }}
              </p>
              <p class="text-sm text-n-slate-11">
                {{ t('TICKETS.SLA.AVERAGE_RESPONSES_PER_TICKET') }}
              </p>
            </div>
          </div>
        </div>

        <!-- SLA Events Timeline -->
        <div class="bg-n-solid-2 rounded-lg p-4 mb-6">
          <h3 class="text-lg font-semibold text-n-slate-12 mb-3">
            {{ t('TICKETS.SLA.RECENT_SLA_EVENTS') }}
          </h3>
          <div
            v-if="recentEvents.length > 0"
            class="space-y-1 relative overflow-y-auto max-h-[300px]"
          >
            <div
              v-for="event in recentEvents"
              :key="event.id"
              class="cursor-pointer"
              @click="navigateToTicket(event.ticket_id)"
            >
              <SlaComponent
                :event="event"
                show-ticket-name="true"
                show-details="true"
                show-icon="true"
                show-border="true"
                show-event-type="true"
              />
            </div>
          </div>
          <div v-else class="text-center py-8">
            <p class="text-n-slate-11">
              {{ t('TICKETS.SLA.NO_RECENT_EVENTS') }}
            </p>
          </div>
        </div>

        <!-- Tickets with SLA Issues -->
        <div
          v-if="problematicTickets.length > 0"
          class="bg-n-solid-2 rounded-lg p-6"
        >
          <h3 class="text-lg font-semibold text-n-slate-12 mb-4">
            {{ t('TICKETS.SLA.TICKETS_WITH_SLA_ISSUES') }}
          </h3>
          <div class="space-y-4 relative overflow-y-auto max-h-[500px]">
            <div
              v-for="ticket in problematicTickets"
              :key="ticket.id"
              class="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg cursor-pointer"
              @click="navigateToTicket(ticket.id)"
            >
              <div class="flex items-center justify-between mb-2">
                <h4 class="font-medium text-n-slate-12">
                  {{ t('TICKETS.SLA.TICKET_NUMBER', { number: ticket.id })
                  }}{{ t('TICKETS.COLON') }} {{ ticket.title }}
                </h4>
                <span
                  class="inline-flex px-2 py-1 text-xs font-medium rounded-full"
                  :class="getSlaStatusClass(ticket.sla_status)"
                >
                  {{ getSlaStatusText(ticket.sla_status) }}
                </span>
              </div>
              <div
                class="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-n-slate-11"
              >
                <div>
                  <p class="font-medium">
                    {{ t('TICKETS.SLA.SLA_POLICY')
                    }}{{ t('TICKETS.COLON') }}
                  </p>
                  <p>{{ ticket.sla_policy_name }}</p>
                </div>
                <div>
                  <p class="font-medium">
                    {{ t('TICKETS.SLA.AGENT_RESPONSES')
                    }}{{ t('TICKETS.COLON') }}
                  </p>
                  <p>{{ ticket.agent_response_count }}</p>
                </div>
                <div>
                  <p class="font-medium">
                    {{ t('TICKETS.SLA.FIRST_RESPONSE')
                    }}{{ t('TICKETS.COLON') }}
                  </p>
                  <p>
                    {{
                      ticket.first_response_at
                        ? formatDate(ticket.first_response_at)
                        : t('TICKETS.SLA.NONE')
                    }}
                  </p>
                </div>
                <div>
                  <p class="font-medium">
                    {{ t('TICKETS.SLA.LAST_UPDATE')
                    }}{{ t('TICKETS.COLON') }}
                  </p>
                  <p>{{ formatDate(ticket.updated_at) }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Refresh Button -->
        <div class="flex justify-end mt-6">
          <button
            :disabled="loading"
            class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
            @click="refreshData"
          >
            <svg
              v-if="loading"
              class="animate-spin w-4 h-4"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                class="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                stroke-width="4"
              />
              <path
                class="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            <svg
              v-else
              class="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
            <span>{{
              loading ? t('TICKETS.SLA.UPDATING') : t('TICKETS.SLA.UPDATE_DATA')
            }}</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
